import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Mail, Lock, Loader2, AlertCircle, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { api, apiError } from "@/api";

function Shell({ children }) {
  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10 relative overflow-hidden">
      <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-[#F2E6E7] blur-3xl opacity-70" />
      <div className="absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-[#E8CFD1] blur-3xl opacity-50" />
      <div className="relative w-full max-w-md">
        <div className="bg-white rounded-[2rem] p-8 sm:p-10 border border-[#F2E6E7] bc-shadow-hover animate-float-up">
          <div className="flex flex-col items-center text-center mb-8">
            <Logo size={60} />
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}

const inputCls =
  "w-full h-12 pl-11 pr-4 rounded-2xl bg-white border border-[#E8CFD1] outline-none focus:border-[#C58B90] focus:ring-2 focus:ring-[#F2E6E7] transition-[border-color,box-shadow]";
const btn =
  "w-full h-12 rounded-full bg-[#C58B90] text-white font-semibold flex items-center justify-center gap-2 hover:bg-[#A87277] transition-colors disabled:opacity-70";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [devLink, setDevLink] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/auth/forgot-password", { email });
      setSent(true);
      if (res.data.reset_link) setDevLink(res.data.reset_link);
      toast.success("Pedido enviado 💗");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Shell>
      <h1 className="font-serif text-2xl text-center mb-2">Esqueci minha senha</h1>
      {sent ? (
        <div className="text-center space-y-4" data-testid="forgot-success">
          <p className="text-sm text-[#7A7571]">
            Se este email estiver cadastrado, você receberá um link para redefinir sua senha. 💗
          </p>
          {devLink && (
            <div className="rounded-2xl bg-[#F2E6E7] p-4 text-left">
              <p className="text-xs text-[#8A5F63] mb-2">Email ainda não configurado — use este link para testar:</p>
              <Link to={devLink.replace(window.location.origin, "")} data-testid="dev-reset-link" className="text-xs text-[#C58B90] break-all underline">
                {devLink}
              </Link>
            </div>
          )}
          <Link to="/login" className="inline-flex items-center gap-1 text-sm text-[#C58B90] font-semibold">
            <ArrowLeft size={16} /> Voltar ao login
          </Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <p className="text-sm text-[#7A7571] text-center mb-2">
            Informe seu email e enviaremos um link para criar uma nova senha.
          </p>
          <div className="relative">
            <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#C58B90]" />
            <input
              className={inputCls}
              type="email"
              placeholder="Seu email"
              data-testid="forgot-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <button className={btn} disabled={loading} data-testid="forgot-submit">
            {loading && <Loader2 size={17} className="animate-spin" />} Enviar link
          </button>
          <Link to="/login" className="block text-center text-sm text-[#C58B90] font-semibold">
            Voltar ao login
          </Link>
        </form>
      )}
    </Shell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await api.post("/auth/reset-password", { token, new_password: password });
      toast.success(res.data.message);
      navigate("/login");
    } catch (e) {
      setError(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Shell>
      <h1 className="font-serif text-2xl text-center mb-4">Criar nova senha</h1>
      {!token && (
        <p className="text-sm text-center text-[#C0555A]">Link inválido. Solicite um novo em "Esqueci minha senha".</p>
      )}
      {error && (
        <div data-testid="reset-error" className="mb-4 flex items-start gap-2 rounded-2xl bg-[#FBEDEE] border border-[#E8CFD1] px-4 py-3 text-sm text-[#A05055]">
          <AlertCircle size={17} className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}
      {token && (
        <form onSubmit={submit} className="space-y-4">
          <div className="relative">
            <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#C58B90]" />
            <input
              className={inputCls}
              type="password"
              placeholder="Nova senha (mín. 6 caracteres)"
              data-testid="reset-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          <button className={btn} disabled={loading} data-testid="reset-submit">
            {loading && <Loader2 size={17} className="animate-spin" />} Redefinir senha
          </button>
        </form>
      )}
    </Shell>
  );
}
